// deps
import { useCallback, useContext } from "react";

// internal-deps
import {
    RenderingDetails,
    ReviewedCartApiResponse,
    SavingsLineItemData,
    SavingsRightSectionType,
    ApplyCouponsWidgetInfo,
    GiftingCartApiResponse,
    GiftingApplyCouponWidgetInfo,
} from "@swiggy-private/cart-types";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";

// contexts
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
import { CartContext } from "@cart-client/contexts/CartContext";

// helpers
import {
    canShowUnlockCouponComms,
    getAppliedCouponIcon,
    getAppliedCouponSubtitleText,
    getCombinedSavingsAndCountForGiftingCoupons,
    showCouponCodesListInGiftingCartSavingsCorner,
    getApplyCouponText,
    getApplyCouponTextForGiftingCart,
    getMROFallbackTextForLockedCoupon,
    getMROApplyRestaurantCouponText,
} from "@cart-client/helpers/Coupon";

// constants
import { APPLY_COUPON_TEXTS, CouponType } from "@cart-client/constants/Coupon";
import { CART_V2_APPLY_COUPON_ICON } from "@cart-client/constants/index";

// utils
import { format } from "@swiggy-private/js-utils";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";

// hooks
import { useCouponSavingsActions } from "@cart-client/hooks/useCouponSavingsActions";
import { useGiftingCouponCodeAnalytics } from "./useGiftingCouponCodeAnalytics";

type ApplyCouponWidgetData =
    | ReviewedCartApiResponse["apply_coupon_widget_info"]
    | GiftingCartApiResponse["apply_coupon_widget_info"];

export interface UseDirectCouponReturnType {
    getDirectCouponSavingsItem: (
        isCouponLockedWidgetVariant: boolean,
        renderingDetails: RenderingDetails,
        applyCouponWidgetData: ApplyCouponWidgetData,
        couponType?: CouponType,
    ) => SavingsLineItemData;
}

export const useDirectCouponSavingsData = (
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
    onSavingsLineItemLoad: (
        title?: string,
        subtitle?: string,
        isPaymentCouponWidget?: boolean,
        restaurantId?: string | number,
    ) => void,
    isNewPORFlow: boolean,
): UseDirectCouponReturnType => {
    const {
        onViewAllCouponsClick,
        onDirectApplyCouponCtaClick,
        onCouponLockedLineItemLoad,
        onDirectCouponLineItemLoad,
    } = useCouponSavingsActions({ applyPaymentCouponWidgetData });
    const { data: cartData } = useContext(CartFlowContext);
    const { cartState } = useContext(CartContext);
    const { reviewedCart } = cartState;
    const { handleImpressionForGiftingCouponCode } = useGiftingCouponCodeAnalytics();
    const isGiftingCart = isGiftingCartSource(cartData?.cartSource);
    const isMROCart = isMROFlow(cartData?.cartSource);

    // coupon locked widget item data
    const getCouponLockedWidgetItemData = useCallback(
        (renderingDetails: RenderingDetails, applyCouponWidgetData: ApplyCouponWidgetData): SavingsLineItemData => {
            let title = {
                value: renderingDetails.display_text ?? APPLY_COUPON_TEXTS.APPLY_COUPON,
            };

            // override the fallback text for MRO cart if it is a locked coupon widget
            if (isMROCart) {
                title = getMROFallbackTextForLockedCoupon(
                    reviewedCart as MROReviewedCart,
                    renderingDetails,
                    applyCouponWidgetData,
                );
            }

            const subtitle = canShowUnlockCouponComms(applyCouponWidgetData)
                ? {
                      value: format(
                          APPLY_COUPON_TEXTS.UNLOCK_COUPON_WITH_X,
                          applyCouponWidgetData?.coupon_code,
                          applyCouponWidgetData?.amount_to_add,
                      ),
                  }
                : undefined;

            const rightSection = {
                type: SavingsRightSectionType.ICON,
            };

            return {
                icon: renderingDetails.icon || CART_V2_APPLY_COUPON_ICON,
                title,
                subtitle,
                rightSection,
                onLineItemClick: () => {
                    onViewAllCouponsClick(true, false, applyCouponWidgetData);
                },
                onLoad: () => {
                    onCouponLockedLineItemLoad();
                    onSavingsLineItemLoad(
                        title.value,
                        subtitle?.value,
                        false,
                        applyCouponWidgetData?.mroChildCartRestaurantId,
                    );
                },
                footerCommsStrip: applyCouponWidgetData?.potential_coupon_info,
            };
        },
        [isMROCart, onCouponLockedLineItemLoad, onSavingsLineItemLoad, onViewAllCouponsClick, reviewedCart],
    );

    const computeApplyCouponText = useCallback(
        (
            couponType: CouponType,
            applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"],
            applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
        ) => {
            if (isGiftingCart) {
                return getApplyCouponTextForGiftingCart(applyCouponWidgetData as GiftingApplyCouponWidgetInfo);
            }
            if (isMROCart) {
                return getMROApplyRestaurantCouponText(reviewedCart as MROReviewedCart, applyCouponWidgetData);
            }
            return getApplyCouponText(couponType, applyCouponWidgetData, applyPaymentCouponWidgetData);
        },
        [isGiftingCart, isMROCart, reviewedCart],
    );

    const getApplyCTAText = useCallback(
        (applyCouponWidgetData: ApplyCouponWidgetData) => {
            if (isGiftingCart) {
                const { count } = getCombinedSavingsAndCountForGiftingCoupons(
                    (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes,
                );
                return count > 1 ? APPLY_COUPON_TEXTS.APPLY_ALL : APPLY_COUPON_TEXTS.APPLY;
            }
            return APPLY_COUPON_TEXTS.APPLY;
        },
        [isGiftingCart],
    );

    // Default direct coupon data
    const getDefaultDirectCouponItemData = useCallback(
        (
            icon: string,
            applyCouponWidgetData: ApplyCouponWidgetData,
            couponType: CouponType = CouponType.RESTAURANT,
        ): SavingsLineItemData => {
            const isPaymentCouponWidget = couponType === CouponType.PAYMENT;
            const title = {
                value: computeApplyCouponText(couponType, applyCouponWidgetData, applyPaymentCouponWidgetData),
            };

            const subtitle = {
                value: getAppliedCouponSubtitleText(couponType, isNewPORFlow),
                icon: true,
                onClick: () => {
                    onViewAllCouponsClick(false, isPaymentCouponWidget, applyCouponWidgetData);
                },
            };

            const rightSection = {
                type: SavingsRightSectionType.BUTTON,
                value: getApplyCTAText(applyCouponWidgetData),
                onClick: () => onDirectApplyCouponCtaClick({ isPaymentCouponWidget, applyCouponWidgetData }),
            };

            let couponCodeList;
            if (showCouponCodesListInGiftingCartSavingsCorner(applyCouponWidgetData)) {
                couponCodeList = {
                    applied: false,
                    codes: (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes,
                    impressionEventCallback: handleImpressionForGiftingCouponCode,
                };
            }

            return {
                icon: icon || getAppliedCouponIcon(couponType),
                title,
                subtitle,
                rightSection,
                onLoad: () => {
                    onDirectCouponLineItemLoad(isPaymentCouponWidget, applyCouponWidgetData);
                    onSavingsLineItemLoad(
                        title.value,
                        subtitle.value,
                        isPaymentCouponWidget,
                        applyCouponWidgetData?.mroChildCartRestaurantId,
                    );
                },
                footerCommsStrip: !isPaymentCouponWidget ? applyCouponWidgetData?.potential_coupon_info : undefined,
                couponCodeList,
            };
        },
        [
            computeApplyCouponText,
            applyPaymentCouponWidgetData,
            isNewPORFlow,
            getApplyCTAText,
            onViewAllCouponsClick,
            onDirectApplyCouponCtaClick,
            onDirectCouponLineItemLoad,
            onSavingsLineItemLoad,
            handleImpressionForGiftingCouponCode,
        ],
    );

    const getDirectCouponSavingsItem = useCallback(
        (
            isCouponLockedWidgetVariant: boolean,
            renderingDetails: RenderingDetails,
            applyCouponWidgetData: ApplyCouponWidgetData,
            couponType?: CouponType,
        ) => {
            if (isCouponLockedWidgetVariant) {
                return getCouponLockedWidgetItemData(renderingDetails, applyCouponWidgetData);
            }
            return getDefaultDirectCouponItemData(renderingDetails.icon, applyCouponWidgetData, couponType);
        },
        [getCouponLockedWidgetItemData, getDefaultDirectCouponItemData],
    );

    return { getDirectCouponSavingsItem };
};
